"use client";

import * as React from "react";
import { LogFilters } from "./log-filters";
import { LogsTable } from "./logs-table";
import { LogDetailsDrawer } from "./log-details-drawer";
import { useLogFilters } from "@/hooks/use-log-filters";
import { useLogQuery, type LogQueryJumpContext } from "@/hooks/use-log-query";
import { UrlFiltersProvider } from "@/hooks/use-url-filters";
import { estimatePageCount } from "@/utils/pagination";
import type { FilterOptionsApiResponse } from "@/types/api";

function LogsClientInner() {
  const { filters, setFilters, isPending } = useLogFilters();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [filterOptions, setFilterOptions] = React.useState<FilterOptionsApiResponse | null>(null);
  const [jumpContext, setJumpContext] = React.useState<LogQueryJumpContext | null>(null);
  const [isCountsLoading, setIsCountsLoading] = React.useState(false);

  const {
    data,
    isLoading,
    error,
    refetch,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    hasPreviousPage,
    hasNextPage,
    currentPage,
    knownPageCount,
    pageSize,
    setPageSize,
  } = useLogQuery(filters, jumpContext);

  const showLoading = isLoading || isPending;

  const handleOptionsChange = React.useCallback((options: FilterOptionsApiResponse | null) => {
    setFilterOptions(options);
  }, []);

  const handleCountsLoadingChange = React.useCallback((loading: boolean) => {
    setIsCountsLoading(loading);
  }, []);

  const pageRecords = data?.records.length ?? 0;
  // Page 1 with no further cursor pages is the complete filtered set.
  const pageIsCompleteResult =
    !showLoading && !error && currentPage === 1 && !hasNextPage;

  const apiMatchingCount = filterOptions?.matchingCount ?? null;
  const apiPartial = filterOptions?.matchingCountIsPartial ?? false;
  const hasActiveFilters = Boolean(
    filters.userIds?.length ||
      filters.routes?.length ||
      filters.methods?.length ||
      filters.errorStatuses?.length ||
      filters.statusCodes?.length ||
      (filters.responseStored && filters.responseStored !== "all") ||
      (filters.fixedStatus && filters.fixedStatus !== "all") ||
      filters.createdFrom ||
      filters.createdTo ||
      filters.updatedFrom ||
      filters.updatedTo ||
      filters.search,
  );

  // While filtered counts are in flight, hide totals so we never flash "0+".
  // Unfiltered (main page) keeps using the fast table total immediately.
  const matchingCount =
    isCountsLoading && hasActiveFilters
      ? null
      : pageIsCompleteResult
        ? pageRecords
        : !apiPartial && apiMatchingCount !== null
          ? apiMatchingCount
          : (apiMatchingCount ?? pageRecords);

  const matchingCountIsPartial =
    isCountsLoading && hasActiveFilters
      ? true
      : pageIsCompleteResult
        ? false
        : apiPartial;

  // Prefer exact filter count for page numbers (e.g. 68 → 3 pages at size 25).
  // Fall back to cursor-known pages when the count is still a sample.
  const displayPageCount = estimatePageCount(
    matchingCount ?? 0,
    pageSize,
    hasNextPage ? Math.max(knownPageCount, currentPage + 1) : currentPage,
    matchingCountIsPartial,
  );

  React.useEffect(() => {
    if (
      (isCountsLoading && hasActiveFilters) ||
      matchingCountIsPartial ||
      matchingCount == null ||
      matchingCount <= 0
    ) {
      setJumpContext(null);
      return;
    }
    setJumpContext({
      totalCount: matchingCount,
      totalPages: displayPageCount,
      canJumpToEnd: true,
    });
  }, [
    matchingCount,
    matchingCountIsPartial,
    displayPageCount,
    isCountsLoading,
    hasActiveFilters,
  ]);

  // Only bounce back if page 2+ returned empty *and* Dynamo said there was no
  // next cursor (true dead end). Do not fight in-flight navigations.
  React.useEffect(() => {
    if (showLoading || error) return;
    if (currentPage > 1 && pageRecords === 0 && !hasNextPage) {
      goToPage(1);
    }
  }, [showLoading, error, currentPage, pageRecords, hasNextPage, goToPage]);

  const canGoNext = hasNextPage && currentPage < displayPageCount;
  const canGoPrevious = hasPreviousPage && currentPage > 1;
  // Only hide page buttons while a *filter* match count is still resolving.
  // Main page uses the fast total and must not wait on the live recount.
  const isPageCountLoading =
    filterOptions == null || (hasActiveFilters && (isCountsLoading || matchingCount == null));

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4 sm:p-6">
      <div className="shrink-0">
        <LogFilters
          onOptionsChange={handleOptionsChange}
          onCountsLoadingChange={handleCountsLoadingChange}
          countOverride={
            filterOptions && !(isCountsLoading && hasActiveFilters)
              ? {
                  totalCount: filterOptions.totalCount,
                  matchingCount: matchingCount ?? 0,
                  matchingCountIsPartial,
                }
              : null
          }
        />
      </div>

      <LogsTable
        records={data?.records ?? []}
        isLoading={showLoading}
        error={error}
        onRetry={refetch}
        onRowClick={setSelectedId}
        sortBy={filters.sortBy ?? "updatedAt"}
        sortDirection={filters.sortDirection ?? "desc"}
        onSortChange={(sortBy, sortDirection) => setFilters({ sortBy, sortDirection })}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        currentPage={currentPage}
        knownPageCount={displayPageCount}
        hasPreviousPage={canGoPrevious}
        hasNextPage={canGoNext}
        onPreviousPage={goToPreviousPage}
        onNextPage={goToNextPage}
        onGoToPage={goToPage}
        resultMayBePartial={data?.metadata.resultMayBePartial ?? false}
        routeGroupingEnabled={true}
        isPageCountLoading={isPageCountLoading}
      />

      <LogDetailsDrawer id={selectedId} onOpenChange={(open) => !open && setSelectedId(null)} />
    </div>
  );
}

export function LogsClient() {
  return (
    <UrlFiltersProvider>
      <LogsClientInner />
    </UrlFiltersProvider>
  );
}
